import { call, put, takeLatest } from 'redux-saga/effects';
import { GET_MAP_LOCATIONS, GET_MAP_LOCATIONS_SUCCESS } from './map.types';
import { mapService } from './map.service';
import { httpRequestsOnLoadActions } from '../http_requests_on_load';
import { httpRequestsOnSuccessActions } from '../http_requests_on_success';
import { httpRequestsOnErrorsActions } from '../http_requests_on_errors';
import { addressPlace, isUSAddress } from 'utils';

/** `GET /api/map/pins` returns tags as `{ id, name, color }`; the map renders plain tag names. */
const toTagNames = (tags) => (Array.isArray(tags) ? tags.map((t) => t?.name).filter(Boolean) : []);

/** Coerce to a finite number (handles numeric strings); NaN for anything missing/unparseable.
 *  Guards null/undefined/'' explicitly since `Number(null)` / `Number('')` are `0`, not NaN. */
const num = (v) => {
    if (v === null || v === undefined || v === '') return NaN;
    const n = Number(v);
    return Number.isFinite(n) ? n : NaN;
};

const isValidLat = (v) => Number.isFinite(v) && v >= -90 && v <= 90;
const isValidLng = (v) => Number.isFinite(v) && v >= -180 && v <= 180;

/**
 * Envelopes that cover the US (contiguous 48, Alaska, Hawaii, Puerto Rico) — loose on purpose:
 * they only decide which reading of a broken coordinate pair is plausible, not where a pin goes.
 */
const US_BOUNDS = [
    { minLat: 24, maxLat: 50, minLng: -125, maxLng: -66 },
    { minLat: 51, maxLat: 72, minLng: -180, maxLng: -129 },
    { minLat: 18, maxLat: 23, minLng: -161, maxLng: -154 },
    { minLat: 17.5, maxLat: 18.6, minLng: -67.5, maxLng: -65 },
];
const inUS = ({ lat, lng }) => US_BOUNDS.some((b) => lat >= b.minLat && lat <= b.maxLat && lng >= b.minLng && lng <= b.maxLng);

/**
 * Repair coordinate pairs that were stored broken, so a pin lands where its address is:
 *   - strings are coerced by `num` before this runs;
 *   - US addresses: if the stored pair isn't in the US, try the swapped and sign-flipped readings
 *     (lat/lng swapped, positive longitude, negative latitude, and combinations) and take the first
 *     that is. If none is, the pair is left as is — it's a wrong geocode, not a typo, and only
 *     re-geocoding the address can fix it;
 *   - anything else: swap only when latitude is out of range but longitude is a valid latitude.
 */
const fixCoords = (lat, lng, usAddress) => {
    if (usAddress && Number.isFinite(lat) && Number.isFinite(lng) && !inUS({ lat, lng })) {
        const readings = [
            { lat: lng, lng: lat },
            { lat, lng: -lng },
            { lat: -lat, lng },
            { lat: -lat, lng: -lng },
            { lat: lng, lng: -lat },
            { lat: -lng, lng: lat },
            { lat: -lng, lng: -lat },
        ];
        const fixed = readings.find(inUS);
        if (fixed) return fixed;
    }
    return !isValidLat(lat) && isValidLat(lng) ? { lat: lng, lng: lat } : { lat, lng };
};

/** `{ city, state }` for a pin — `null` when unknown, so the filters' option lists skip it. */
const placeOf = (pin) => {
    const { city, state } = addressPlace(pin);
    return { city: city || null, state: state || null };
};

/** Coordinates + place for a pin — the place decides whether US-specific repairs apply. */
const locate = (pin) => {
    const place = placeOf(pin);
    return { ...fixCoords(num(pin?.lat), num(pin?.lng), isUSAddress(pin, place)), ...place };
};

/** A pin is mappable only with finite, in-range coordinates — otherwise Google Maps throws. */
const isMappable = (p) => isValidLat(p?.lat) && isValidLng(p?.lng);

/** Normalize an API client pin to the shape the map component consumes. */
const normalizeClient = (c) => {
    const { lat, lng, city, state } = locate(c);
    return {
        id: c?.id,
        code: c?.code,
        firstName: c?.firstName,
        middleName: c?.middleName,
        lastName: c?.lastName,
        avatarUrl: c?.avatarUrl,
        status: c?.status,
        tags: toTagNames(c?.tags),
        lat,
        lng,
        formattedAddress: c?.formattedAddress,
        // From the API's city/state, else parsed from formattedAddress (State/City filters).
        city,
        state,
        // Staff assigned to this client → "Assigned to" line on the info card.
        assignedTo: c?.assignments?.map((a) => a?.personName).filter(Boolean).join(', ') || null,
    };
};

/** Normalize an API staff pin to the shape the map component consumes. */
const normalizeStaff = (s) => {
    const { lat, lng, city, state } = locate(s);
    return {
        id: s?.id,
        firstName: s?.firstName,
        middleName: s?.middleName,
        lastName: s?.lastName,
        avatarUrl: s?.avatarUrl,
        status: s?.status,
        tags: toTagNames(s?.tags),
        lat,
        lng,
        formattedAddress: s?.formattedAddress,
        city,
        state,
        // Staff card extras: job title (type label) and work location (department name).
        title: s?.title,
        workLocation: s?.workLocation,
        // Clients this staff serves → dashed connectors (id + role) drawn from the staff pin, and
        // the "Assigned: N Clients (…)" summary on the staff info card (needs the client name).
        clients: Array.isArray(s?.assignments)
            ? s.assignments.map((a) => ({ id: a?.personId, name: a?.personName, role: a?.role }))
            : [],
    };
};

function* getMapLocations({ payload, type }) {
    if (payload?.load !== 'noLoad') {
        yield put(httpRequestsOnLoadActions.appendLoading(type));
    }
    yield put(httpRequestsOnErrorsActions.removeError(type));
    try {
        const res = yield call(mapService.getMapPinsService, payload?.params);
        yield put({
            type: GET_MAP_LOCATIONS_SUCCESS,
            payload: {
                // Drop any pin without finite, in-range coordinates — an unmappable pin would make
                // Google Maps' bounds.extend / LatLng throw "in property lat: not a number".
                clients: (res?.data?.clients || []).map(normalizeClient).filter(isMappable),
                staff: (res?.data?.staff || []).map(normalizeStaff).filter(isMappable),
            },
        });
        yield put(httpRequestsOnSuccessActions.appendSuccess(type));
        yield put(httpRequestsOnLoadActions.removeLoading(type));
        yield put(httpRequestsOnErrorsActions.removeError(type));
    } catch (err) {
        yield put(httpRequestsOnLoadActions.removeLoading(type));
        yield put(httpRequestsOnErrorsActions.appendError(type));
    }
}

export const watchMap = function* watchMap() {
    yield takeLatest(GET_MAP_LOCATIONS, getMapLocations);
};
